import React, { useEffect, useState } from "react";
import apiService from "../../services/ApiService";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";
import TailwindInput from "../../components/Tailwind/TailwindInput";
import TailwindButton from "../../components/Tailwind/TailwindButton";
import { cn } from "../../lib/utils";

const ResetPassword = ({ reset_succeeds, forbidden_page }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [validating, setValidating] = useState(true);
  const [passwordError, setPasswordError] = useState({
    message: '',
    variant: ''
  });

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    apiService.checkValidReset(sp.get('access_token'), sp.get('token_type')).then(async (response) => {
      if (response) {
        if (!response.ok) {
          forbidden_page();
          sp.delete('access_token');
          sp.delete('token_type');
          sp.delete('context');
          window.location.search = sp.toString();
        } else {
          setValidating(false);
        }
      }
    });
  }, []);

  const SignupSchema = Yup.object().shape({
    password: Yup.string().required("Password is required"),
    password_confirmation: Yup.string().required("Password confirmation is required"),
  });

  const formik = useFormik({
    initialValues: {
      password: "",
      password_confirmation: "",
    },
    validationSchema: SignupSchema,
    onSubmit: () => {
      if (values.password == values.password_confirmation) {
        const passwordValidation = new RegExp(
          /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/
        );
        const isValidPassword = passwordValidation.test(values.password);
        if (isValidPassword) {
          setPasswordError({ message: '', variant: '' });
          setTimeout(() => {
            const sp = new URLSearchParams(window.location.search);
            apiService.resetPassword(values.password).then(async (response) => {
              if (response) {
                setSubmitting(false);
                if (response.ok) {
                  reset_succeeds();
                  sp.delete('access_token');
                  sp.delete('token_type');
                  sp.delete('context');
                  window.location.search = sp.toString();
                } else {
                  const elem = await response.json();
                  setPasswordError({
                    message: elem.detail,
                    variant: 'error'
                  });
                }
              }
            });
          }, 500);
        } else {
          setSubmitting(false);
          setPasswordError({
            message: 'The new password must contain 8 characters with at least one lowercase, one uppercase, one digit and one special character',
            variant: 'error'
          });
        }
      } else {
        setSubmitting(false);
        setPasswordError({
          message: "The new password and the new password confirmation doesn't match",
          variant: 'error'
        });
      }
    },
  });

  const { errors, touched, values, handleSubmit, isSubmitting, getFieldProps, setSubmitting, setFieldValue } = formik;

  const clearError = () => setPasswordError({ message: '', variant: '' });

  if (validating) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-5 animate-fade-in">
          <img
            width="180"
            height="56"
            src="/logo-text-black.png"
            alt="Peerception"
          />
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-400">Verifying your link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 animate-fade-in">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">
          {/* Header */}
          <div className="px-8 pt-10 pb-6 text-center">
            <img
              width="220"
              height="68"
              src="/logo-text-black.png"
              alt="Peerception"
              className="mx-auto mb-6"
            />
            <div className="size-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
              <span className="material-symbols-outlined text-[28px] text-amber-600">lock_reset</span>
            </div>
            <h1 className="text-xl font-bold text-slate-900">Reset your password</h1>
            <p className="text-sm text-slate-500 mt-1">Enter a new password for your account</p>
          </div>

          {/* Body */}
          <div className="px-8 pb-8">
            <FormikProvider value={formik}>
              <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                <div className="flex flex-col gap-4">
                  <div className="relative">
                    <TailwindInput
                      fullWidth
                      type={showPassword ? "text" : "password"}
                      label="New password"
                      placeholder="Create a new password"
                      {...getFieldProps("password")}
                      error={touched.password && errors.password ? String(errors.password) : undefined}
                      onChange={(e) => {
                        setFieldValue("password", e.target.value);
                        clearError();
                      }}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-9 text-slate-400 hover:text-slate-600 transition-colors"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      <span className="material-symbols-outlined text-[20px]">
                        {showPassword ? 'visibility' : 'visibility_off'}
                      </span>
                    </button>
                  </div>

                  <div className="relative">
                    <TailwindInput
                      fullWidth
                      type={showPasswordConfirmation ? "text" : "password"}
                      label="Confirm new password"
                      placeholder="Confirm your new password"
                      {...getFieldProps("password_confirmation")}
                      error={touched.password_confirmation && errors.password_confirmation ? String(errors.password_confirmation) : undefined}
                      onChange={(e) => {
                        setFieldValue("password_confirmation", e.target.value);
                        clearError();
                      }}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-9 text-slate-400 hover:text-slate-600 transition-colors"
                      onClick={() => setShowPasswordConfirmation((prev) => !prev)}
                    >
                      <span className="material-symbols-outlined text-[20px]">
                        {showPasswordConfirmation ? 'visibility' : 'visibility_off'}
                      </span>
                    </button>
                  </div>

                  <p className="text-[11px] text-slate-400 px-1 -mt-1">
                    Must contain 8+ characters with uppercase, lowercase, digit and special character
                  </p>

                  {passwordError.message && (
                    <div className={cn(
                      "flex items-center gap-2 p-3 rounded-lg text-sm",
                      passwordError.variant === 'error'
                        ? "bg-red-50 text-red-800 border border-red-200"
                        : "bg-green-50 text-green-800 border border-green-200"
                    )}>
                      <span className="material-symbols-outlined text-[18px] shrink-0">error</span>
                      {passwordError.message}
                    </div>
                  )}

                  <TailwindButton
                    fullWidth
                    size="lg"
                    type="submit"
                    variant="dark"
                    icon="lock_reset"
                    disabled={isSubmitting}
                    className="mt-1"
                  >
                    {isSubmitting ? 'Resetting...' : 'Reset password'}
                  </TailwindButton>
                </div>
              </Form>
            </FormikProvider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
